import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AppConfigService, AuthenticationService } from '@alfresco/adf-core';
import { Observable } from 'rxjs';

/**
 * Frontend client for the {@code /api/wf/hotfolder/unmatched} endpoints
 * — Phase B (step 1) park mode.
 *
 * <p>Backs the "Unmatched scans" list page that lets records officers
 * dispose of files that landed in {@code .unmatched/} (no auto-match):
 *
 * <ul>
 *   <li>{@link #list} — show the queue</li>
 *   <li>{@link #attachToInstance} — manually attach to an existing mail</li>
 *   <li>{@link #startNewInstance} — spawn a new register-mail task with the file</li>
 *   <li>{@link #deleteFile} — discard (file moves to {@code .failed/})</li>
 * </ul>
 */
@Injectable({ providedIn: 'root' })
export class WfUnmatchedHotfolderApi {

  private readonly base: string;

  constructor(
    private http: HttpClient,
    private appConfig: AppConfigService,
    private authService: AuthenticationService
  ) {
    const apiBase = (this.appConfig.get('skyplus.apiUrl', '/bpm/api') as string) || '/bpm/api';
    this.base = `${apiBase}/wf/hotfolder/unmatched`;
  }

  /** Files currently in {@code .unmatched/} for the given workflow. */
  list(wfKey: string): Observable<UnmatchedFile[]> {
    return this.http.get<UnmatchedFile[]>(
      `${this.base}?wfKey=${encodeURIComponent(wfKey)}`,
      { headers: this.authHeaders() }
    );
  }

  /**
   * Attach the named file to an existing instance — by either Long
   * {@code instanceId} or the {@code businessKey} string the officer
   * sees in the task header. Provide one OR the other.
   */
  attachToInstance(wfKey: string, fileName: string,
                   target: { instanceId?: number; businessKey?: string }): Observable<void> {
    const body = { wfKey, fileName, ...target };
    return this.http.post<void>(`${this.base}/attach`, body, {
      headers: this.jsonHeaders()
    });
  }

  /**
   * Spawn a fresh instance from the file. Lands at register-mail with
   * the document already attached. Officer fills the missing delivery
   * metadata there. Returns the new instance summary so the UI can
   * link the officer straight to the new register task.
   */
  startNewInstance(wfKey: string, fileName: string): Observable<InstanceSummary> {
    return this.http.post<InstanceSummary>(`${this.base}/start-instance`,
      { wfKey, fileName },
      { headers: this.jsonHeaders() }
    );
  }

  /**
   * Discard the file — moves it to {@code .failed/} so the on-disk
   * audit record is preserved but it disappears from the queue.
   */
  /**
   * Discard the file — moves it to {@code .failed/} so the on-disk
   * audit record is preserved but it disappears from the queue.
   */
  deleteFile(wfKey: string, fileName: string): Observable<void> {
    const url = `${this.base}?wfKey=${encodeURIComponent(wfKey)}`
              + `&fileName=${encodeURIComponent(fileName)}`;
    return this.http.delete<void>(url, { headers: this.authHeaders() });
  }

  /**
   * Fetch the file as a Blob so we can preview it in a new tab via
   * {@code URL.createObjectURL}. The authenticated request attaches the
   * Bearer token; opening the raw {@code /file} URL directly wouldn't,
   * because tabs don't inherit headers.
   */
  previewFile(wfKey: string, fileName: string): Observable<Blob> {
    const url = `${this.base}/file?wfKey=${encodeURIComponent(wfKey)}`
              + `&fileName=${encodeURIComponent(fileName)}`;
    return this.http.get(url, {
      headers: this.authHeaders(),
      responseType: 'blob'
    });
  }
  // ────────────────────────────────────────────────────────────────────────
  // Header helpers (no HTTP interceptor — per-request token attach)
  // ────────────────────────────────────────────────────────────────────────

  private authHeaders(): HttpHeaders {
    const token = this.authService.getToken();
    return new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Accept':        'application/json'
    });
  }

  private jsonHeaders(): HttpHeaders {
    return this.authHeaders().set('Content-Type', 'application/json');
  }
}

// ──────────────────────────────────────────────────────────────────────────
// DTOs (mirror the backend shapes)
// ──────────────────────────────────────────────────────────────────────────

export interface UnmatchedFile {
  fileName: string;
  sizeBytes: number;
  droppedAt: string;   // ISO LocalDateTime e.g. "2026-05-16T11:07:41.123"
}

export interface InstanceSummary {
  id: number;
  wfKey: string;
  businessKey: string;
  title?: string;
  status: string;
  currentStage?: string;
  currentTaskId?: string;
  currentTaskName?: string;
  currentAssignee?: string;
  // … other fields the kernel returns; we only type the ones we use
}
